import * as React from "react";
import { cn } from "../lib/cn";

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "size"> {
  /**
   * Stato parziale: nessuna spunta, un trattino. Per una "seleziona tutti" che
   * governa righe selezionate solo in parte. Ha la precedenza su `checked`.
   */
  indeterminate?: boolean;
}

/**
 * Checkbox — la spunta di Ando.
 *
 * Prima era l'`<input type="checkbox">` nudo con `accent-color`: il disegno lo
 * faceva il sistema operativo, quindi il segno cambiava forma fra Windows,
 * macOS e Linux e non rispondeva a nessuno dei nostri token (le classi
 * `rounded border-hairline` che aveva addosso non facevano proprio nulla).
 * Qui il controllo resta nativo — tastiera, `:checked`, `:indeterminate`,
 * semantica di form e lettori di schermo si hanno gratis — ma
 * `appearance-none` ci restituisce il disegno.
 *
 * **Il segno si disegna.** `stroke-dasharray` lungo quanto il percorso e
 * `stroke-dashoffset` che lo spinge tutto fuori a riposo; alla spunta l'offset
 * va a zero e il tratto cresce da in basso a sinistra verso destra, nel verso
 * in cui lo si scriverebbe a mano.
 *
 * Le due proprietà stanno sull'`<svg>`, non sul `<path>`: `peer-checked:`
 * compila in un selettore FRATELLO (`.peer:checked ~ …`) e il path è figlio del
 * fratello, quindi lì non matcherebbe mai. `stroke-dasharray` e
 * `stroke-dashoffset` si ereditano, così il path le riceve — transizione
 * compresa, come accade con `color`.
 *
 * **La curva conta più della durata.** Con un ease-out aggressivo il tratto
 * copre quasi tutta la distanza nel primo terzo e poi striscia: si legge come
 * un'apparizione, non come un gesto. Serve una curva che distribuisca il
 * percorso — da lì `cubic-bezier(0.4, 0, 0.2, 1)`.
 *
 * Entrata 300ms, uscita 150: la durata di una transizione CSS è quella dello
 * stato verso cui si va, quindi basta metterla sul ramo `peer-checked`. Chi
 * agisce aspetta volentieri, chi annulla vuole che sia già finita.
 *
 * **Il segno è bianco su `ando-orange-hover` (#E8650E), non sul pieno di brand.**
 * Bianco su `#FF7A1A` sta a 2,6:1, sotto il 3:1 che WCAG 1.4.11 chiede a un
 * elemento grafico; su `orange-hover` arriva a 3,3:1. L'alternativa era un segno
 * scuro sul pieno (6,4:1, conforme) ma leggeva come nastro adesivo da cantiere.
 *
 * Il nome del token è improprio — nasce come STATO e qui lo usiamo come PIENO —
 * e la cosa giusta sarebbe un token dedicato. Non si fa adesso: un token nuovo
 * vive nel preset, e Tailwind tiene il config IN MEMORIA ricaricandolo quando
 * cambia il config o i sorgenti, MAI quando cambia un preset importato. Un token
 * aggiunto a server acceso non esiste per la pagina: la classe non viene
 * generata, nessun errore, e il riquadro resta bianco col segno bianco sopra.
 * Il token dedicato si aggiunge a server spento, in una passata sua.
 *
 * `indeterminate` non è un attributo HTML, è una proprietà del nodo: va scritta
 * sul DOM, non passata in JSX. Da qui il ref interno.
 *
 * `className` finisce sul **wrapper**, non sull'input. Le classi che i chiamanti
 * passano davvero sono di allineamento (`mt-0.5`, `mt-1`, `shrink-0`): sull'input
 * sposterebbero la scatola DENTRO lo span, e il segno — che è in `absolute
 * inset-0` dello span — resterebbe indietro. La misura del controllo è una sola,
 * decisa qui: è il senso di avere una primitiva invece di 53 riquadri.
 */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, indeterminate = false, ...rest }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);
    React.useEffect(() => {
      if (inputRef.current) inputRef.current.indeterminate = indeterminate;
    }, [indeterminate]);

    const mark =
      "pointer-events-none absolute inset-0 h-4 w-4 text-white transition-[stroke-dashoffset] duration-150 ease-[cubic-bezier(0.4,0,0.2,1)]";

    return (
      <span className={cn("relative inline-flex h-4 w-4 shrink-0 align-middle", className)}>
        <input
          ref={inputRef}
          type="checkbox"
          className={cn(
            "peer h-4 w-4 cursor-pointer appearance-none rounded-xs border border-ink-3 bg-white",
            // Una dichiarazione sola: `transition-colors` e `transition-transform`
            // sono la stessa proprieta CSS, la seconda cancellerebbe la prima.
            "transition-[background-color,border-color,transform] duration-150 hover:border-ink-1",
            "checked:border-ando-orange-hover checked:bg-ando-orange-hover checked:hover:border-ando-orange-hover",
            "indeterminate:border-ando-orange-hover indeterminate:bg-ando-orange-hover indeterminate:hover:border-ando-orange-hover",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1",
            "motion-safe:active:scale-90",
            "disabled:cursor-not-allowed disabled:border-ink-4 disabled:bg-cream-deep disabled:opacity-60",
          )}
          {...rest}
        />
        {/* Spunta — nascosta quando lo stato e parziale: il trattino vince. */}
        <svg
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden
          className={cn(
            mark,
            "[stroke-dasharray:10.5] [stroke-dashoffset:10.5]",
            "peer-checked:[stroke-dashoffset:0] peer-checked:duration-300",
            "peer-indeterminate:[stroke-dashoffset:10.5] peer-indeterminate:duration-100",
          )}
        >
          <path
            d="M4.4 8.3 6.8 10.7 11.6 5.5"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {/* Trattino dello stato parziale — si disegna dal centro verso fuori. */}
        <svg
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden
          className={cn(
            mark,
            "[stroke-dasharray:7] [stroke-dashoffset:7]",
            "peer-indeterminate:[stroke-dashoffset:0] peer-indeterminate:duration-200",
          )}
        >
          <path d="M4.5 8 11.5 8" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
        </svg>
      </span>
    );
  },
);
Checkbox.displayName = "Checkbox";
